import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Row,
  Col,
  InputNumber,
  AutoComplete,
  Card,
  Divider,
  Alert,
  Tag,
  Tooltip,
  message,
  Table,
  Popconfirm,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
  BarcodeOutlined,
  UserOutlined,
  GlobalOutlined,
  TruckOutlined,
  ProductOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { OutboundOrder, OutboundOrderItem, OutboundOrderStatus } from '../pages/OutboundOrderPage';
import apiService from '../../../services/apiService';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Customer {
  value: string;
  label: string;
  code: string;
}

interface Product {
  id: string;
  nameEN: string;
  nameCN: string;
  skuId: string;
  productBarcode?: string;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unitPrice: number;
  availableStock: number;
  defaultAllocateRule: string;
  alternateSkuList: string[]; // 添加備用 SKU 列表
}

interface CountryCode {
  value: string;
  label: string;
}

interface AllocateRule {
  value: string;
  label: string;
}

interface OutboundOrderFormProps {
  visible: boolean;
  order?: OutboundOrder | null;
  customers: Customer[];
  products: Product[];
  couriers: string[];
  countryCodes: CountryCode[];
  allocateRules: AllocateRule[];
  warehouses?: Array<{ id: string; name: string }>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const OutboundOrderForm: React.FC<OutboundOrderFormProps> = ({
  visible,
  order,
  customers,
  products,
  couriers,
  countryCodes,
  allocateRules,
  warehouses = [],
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState<OutboundOrderItem[]>([]);
  const [customerOptions, setCustomerOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [hasStockIssues, setHasStockIssues] = useState(false);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [productOptions, setProductOptions] = useState<Array<{ value: string; label: string; product: Product }>>([]);
  const productSearchTimer = useRef<any>(null);
  const lastQueryRef = useRef<string>('');

  // 初始化表單數據
  useEffect(() => {
    if (visible) {
      if (order) {
        // 編輯模式 - 先調用 detail API 獲取完整數據
        loadOrderDetail(order.id);
      } else {
        // 新增模式
        form.resetFields();
        setItems([]);
        setSelectedCustomer(null);
      }
      // 預設選中第一個倉庫
      if (warehouses && warehouses.length > 0) {
        setWarehouseId(warehouses[0].id);
      }
      // 初始載入產品選項
      fetchProductOptions('');
    }
  }, [visible, order, form, customers]);

  // 載入訂單詳情
  const loadOrderDetail = async (orderId: string) => {
    try {
      const resp: any = await apiService.getOutboundOrder(orderId);
      const orderDetail: any = resp?.data || resp || {};
      
      // 填充表單數據
      form.setFieldsValue({
        customerName: orderDetail.customerName || orderDetail.customerCode,
        customerCode: orderDetail.customerCode,
        courier: orderDetail.courier || orderDetail.deliveryCompany,
        receiver: orderDetail.receiver,
        countryCode: orderDetail.countryCode || 'CN',
        deliveryAddress: orderDetail.deliveryAddress,
        deliveryTrackingNumber: orderDetail.deliveryTrackingNumber || orderDetail.trackingNumber,
        deliveryRemark: orderDetail.deliveryRemark || orderDetail.remark,
        salesChannel: orderDetail.salesChannel,
        status: orderDetail.status,
        remark: orderDetail.remark,
        warehouseId: orderDetail.warehouseId || warehouseId,
      });
      
      // 設置 items 數據
      if (Array.isArray(orderDetail.items)) {
        setItems(orderDetail.items);
      } else {
        setItems([]);
      }
      
      setSelectedCustomer(customers.find(c => c.code === orderDetail.customerCode) || null);
      
    } catch (error) {
      console.error('Failed to load order details:', error);
      message.error('載入訂單詳情失敗');
      
      // 降級處理：使用傳入的基本數據
      form.setFieldsValue({
        customerName: order.customerName,
        customerCode: order.customerCode,
        courier: order.courier,
        receiver: order.receiver,
        countryCode: order.countryCode,
        deliveryAddress: order.deliveryAddress,
        deliveryTrackingNumber: order.deliveryTrackingNumber,
        deliveryRemark: order.deliveryRemark,
        salesChannel: order.salesChannel,
        status: order.status,
        remark: order.remark,
        warehouseId: (order as any)?.warehouseId || warehouseId,
      });
      setItems(order.items || []);
      setSelectedCustomer(customers.find(c => c.code === order.customerCode) || null);
    }
  };

  // 檢查庫存問題
  useEffect(() => {
    const hasIssues = items.some(item => 
      item.quantityBaseUnit > (item.availableStock || 0)
    );
    setHasStockIssues(hasIssues);
  }, [items]);

  // 客戶搜索選項
  useEffect(() => {
    const options = customers.map(customer => ({
      value: customer.label,
      label: `${customer.label} (${customer.code})`
    }));
    setCustomerOptions(options);
  }, [customers]);

  // 處理客戶選擇
  const handleCustomerSelect = (value: string) => {
    const customer = customers.find(c => c.label === value);
    if (customer) {
      setSelectedCustomer(customer);
      form.setFieldValue('customerCode', customer.code);
    }
  };

  // 從後端查產品做為自動完成選項
  const mapBackendToProduct = (p: any): Product => {
    // 依當前選擇倉庫取該倉可用量
    let available = Number(p.availableStock || p.totalStock || 0);
    try {
      if (warehouseId && Array.isArray(p.warehouses)) {
        const wh = p.warehouses.find((w: any) => String(w.warehouseId || w.wid) === String(warehouseId));
        if (wh) {
          available = Number(wh.availableQuantity || wh.available || 0);
        } else {
          // If product not in selected warehouse, set to 0
          available = 0;
        }
      }
    } catch (e) {
      console.error('[OutboundForm] Error processing warehouse data:', e);
    }
    return {
      id: p.id,
      nameEN: p.itemNameEnglish || p.itemName || p.name || '',
      nameCN: p.itemNameChinese || p.itemName || '',
      skuId: p.itemCode || p.sku || '',
      productBarcode: p.barcode || '',
      brand: p.brand || '',
      length: Number(p.length || 0),
      width: Number(p.width || 0),
      height: Number(p.height || 0),
      weight: Number(p.weight || 0),
      unitPrice: Number(p.unitPrice || 0),
      availableStock: available,
      defaultAllocateRule: String(p.batchControlType || 'FIFO').toUpperCase(),
      alternateSkuList: Array.isArray(p.alternateSkuList) ? p.alternateSkuList : [], // 添加備用 SKU 列表
    } as Product;
  };

  const fetchProductOptions = async (q: string) => {
    try {
      lastQueryRef.current = q;
      const resp: any = await apiService.getProducts({ limit: 50, search: q });
      const list: any[] = resp?.products || resp?.data || [];
      const mapped: Product[] = list.map(mapBackendToProduct);
      setProductOptions(
        mapped.map(p => ({ value: p.skuId, label: `${p.skuId} ${p.nameEN || ''}${p.alternateSkuList?.length ? ` [${p.alternateSkuList.join(', ')}]` : ''}`.trim(), product: p }))
      );
    } catch (e) {
      // 降級：若失敗使用傳入 products
      setProductOptions((products || []).map(p => ({ value: p.skuId, label: `${p.skuId} ${p.nameEN || ''} [${p.alternateSkuList.join(', ')}]`.trim(), product: p })));
    }
  };

  // 倉庫切換時，重新根據當前倉庫重算可用庫存
  useEffect(() => {
    if (visible) {
      fetchProductOptions(lastQueryRef.current || '');

      // Update available stock for existing items when warehouse changes
      if (items.length > 0 && warehouseId) {
        console.log('📦 [Warehouse Change] Starting stock update:', { warehouseId, itemCount: items.length });

        const updateItemsStock = async () => {
          try {
            // Get all unique SKUs from current items
            const skus = items.map(item => item.skuId).filter(Boolean);
            if (skus.length === 0) return;

            console.log('🔍 [Warehouse Change] Fetching stock for SKUs:', skus);

            // Fetch updated product data with warehouse context
            // Use limit: 50 to get more results and find exact match
            const promises = skus.map(sku =>
              apiService.getProducts({ limit: 50, search: sku }).catch(() => null)
            );
            const responses = await Promise.all(promises);

            // Create a map of SKU to updated available stock
            const stockMap = new Map<string, number>();
            responses.forEach((resp: any, index: number) => {
              const requestedSku = skus[index];
              if (resp?.products && Array.isArray(resp.products)) {
                // Search for exact SKU match in all returned products
                const exactMatch = resp.products.find((p: any) =>
                  (p.itemCode || p.sku) === requestedSku
                );

                if (exactMatch) {
                  const mapped = mapBackendToProduct(exactMatch);
                  stockMap.set(mapped.skuId, mapped.availableStock);
                  console.log(`✓ Exact match found for ${requestedSku}, stock: ${mapped.availableStock}`);
                } else {
                  console.warn(`✗ No exact match found for "${requestedSku}" in ${resp.products.length} results`);
                }
              }
            });

            console.log('📊 [Warehouse Change] Stock map:', Array.from(stockMap.entries()));

            // Update items with new available stock
            setItems(prevItems => {
              console.log('🔄 [Warehouse Change] Current items:', prevItems.map(i => ({ sku: i.skuId, oldStock: i.availableStock })));

              const updated = prevItems.map(item => {
                if (!item.skuId) return item;
                const newAvailableStock = stockMap.get(item.skuId);

                if (newAvailableStock !== undefined) {
                  console.log(`✅ [Warehouse Change] Updating ${item.skuId}: ${item.availableStock} → ${newAvailableStock}`);
                  return {
                    ...item,
                    availableStock: newAvailableStock,
                    isStockInsufficient: item.quantityBaseUnit > newAvailableStock
                  };
                } else {
                  console.warn(`❌ [Warehouse Change] No stock data for ${item.skuId}`);
                }
                return item;
              });

              console.log('✨ [Warehouse Change] Updated items:', updated.map(i => ({ sku: i.skuId, newStock: i.availableStock })));
              return updated;
            });
          } catch (error) {
            console.error('❌ [Warehouse Change] Error:', error);
          }
        };

        updateItemsStock();
      }
    }
  }, [warehouseId]);

  // 添加產品行項目
  const addItem = () => {
    const newItem: OutboundOrderItem = {
      id: `item_${Date.now()}`,
      productNameEN: '',
      productNameCN: '',
      skuId: '',
      productBarcode: '',
      quantityPackage: 1,
      quantityBaseUnit: 1,
      brand: '',
      length: 0,
      width: 0,
      height: 0,
      weight: 0,
      unitPrice: 0,
      allocateRule: 'FIFO',
      remark: '',
      availableStock: 0,
      isStockInsufficient: false
    };
    setItems([...items, newItem]);
  };

  // 刪除產品行項目
  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // 更新產品行項目
  const updateItem = (index: number, field: keyof OutboundOrderItem, value: any) => {
    const newItems = [...items];
    const nextItem: any = { ...newItems[index], [field]: value };

    // 如果是數量變更，同步兩個數量欄位（因為後端只儲存一個值）
    if (field === 'quantityBaseUnit' || field === 'quantityPackage') {
      nextItem.quantityBaseUnit = value;
      nextItem.quantityPackage = value;
      nextItem.isStockInsufficient = value > (nextItem.availableStock || 0);
    }

    newItems[index] = nextItem;
    setItems(newItems);
  };

  // 處理產品選擇
  const handleProductSelect = (index: number, selected: string, option?: any) => {
    const matched = option?.product as Product | undefined;
    const product = matched || products.find(p => p.id === selected || p.nameEN === selected || p.skuId === selected);
    if (!product) return;

    // 一次性合併更新，避免多次 setState 造成舊狀態覆蓋
    const newItems = [...items];
    const prev = newItems[index] || ({} as OutboundOrderItem);

    // 檢查是否是選擇新產品（SKU改變），若是則重置數量為1，否則保留原數量
    const isNewProduct = prev.skuId && prev.skuId !== product.skuId;
    const defaultQuantity = isNewProduct ? 1 : (prev.quantityBaseUnit || 1);

    newItems[index] = {
      ...prev,
      productNameEN: product.nameEN,
      productNameCN: product.nameCN,
      skuId: product.skuId,
      productBarcode: (product.productBarcode || ''),
      brand: product.brand,
      length: product.length,
      width: product.width,
      height: product.height,
      weight: product.weight,
      unitPrice: product.unitPrice,
      availableStock: product.availableStock,
      allocateRule: product.defaultAllocateRule,
      quantityPackage: defaultQuantity,
      quantityBaseUnit: defaultQuantity,
      isStockInsufficient: defaultQuantity > (product.availableStock || 0),
    };
    setItems(newItems);
  };

  // 產品搜索（後端）
  const handleProductSearch = (value: string) => {
    if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
    productSearchTimer.current = setTimeout(() => fetchProductOptions(value.trim()), 300);
  };

  // 提交表單
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (items.length === 0) {
        message.error('請至少添加一個產品');
        return;
      }

      // 檢查庫存問題
      const stockIssues = items.filter(item => item.isStockInsufficient);
      if (stockIssues.length > 0) {
        const issuesList = stockIssues.map(item => 
          `${item.skuId || '未知SKU'}: 出貨數量 ${item.quantityBaseUnit}, 可用庫存 ${item.availableStock || 0}`
        ).join('\n');
        
        Modal.confirm({
          title: '庫存不足警告',
          icon: <WarningOutlined style={{ color: '#faad14' }} />,
          content: (
            <div>
              <p>以下產品庫存不足，是否仍要繼續提交訂單？</p>
              <pre style={{ 
                background: '#fff7e6', 
                padding: '12px', 
                borderRadius: '4px',
                fontSize: '12px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {issuesList}
              </pre>
              <p style={{ color: '#faad14', marginTop: '12px' }}>
                <WarningOutlined /> 注意：提交後訂單將被標記為庫存不足狀態
              </p>
            </div>
          ),
          okText: '繼續提交',
          cancelText: '返回修改',
          onOk: () => {
            submitOrder(values);
          },
        });
      } else {
        submitOrder(values);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const submitOrder = (values: any) => {
    const totalValue = items.reduce((sum, item) => 
      sum + (item.unitPrice * item.quantityBaseUnit), 0
    );

    const orderData = {
      ...values,
      items: items,
      totalValue: totalValue,
      hasStockIssues: hasStockIssues,
      status: values.status || '待發貨' as OutboundOrderStatus,
      warehouseId: values.warehouseId || warehouseId,
    };

    onSubmit(orderData);
  };

  // 產品表格列定義
  const itemColumns: ColumnsType<OutboundOrderItem> = [
    {
      title: '產品 (用SKU選擇)',
      key: 'productSelect',
      width: 280,
      render: (_, record, index) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          options={productOptions}
          placeholder="選擇 SKU"
          value={record.skuId || undefined}
          optionFilterProp="label"
          filterOption={(input, option) => {
            const label = (option?.label || '').toString().toLowerCase();
            const value = (option?.value || '').toString().toLowerCase();
            return label.includes(input.toLowerCase()) || value.includes(input.toLowerCase());
          }}
          onSearch={handleProductSearch}
          onSelect={(value, option) => handleProductSelect(index, value as string, option)}
        />
      ),
    },
    {
      title: '產品名稱 (EN)',
      key: 'productNameEN',
      width: 220,
      render: (_, record) => (
        <Input value={record.productNameEN ? `${record.skuId ? record.skuId + ' ' : ''}${record.productNameEN}` : ''} readOnly placeholder="自動帶出" />
      ),
    },
    {
      title: '產品名稱 (CN)',
      key: 'productNameCN',
      width: 200,
      render: (_, record) => (
        <Input value={record.productNameCN} readOnly placeholder="自動帶出" />
      ),
    },
    // SKU 由上方下拉選擇，這裡顯示只讀
    {
      title: 'SKU ID',
      key: 'skuId',
      width: 140,
      render: (_, record) => (
        <Input value={record.skuId} readOnly placeholder="自動帶出" />
      ),
    },
    {
      title: '產品條碼',
      key: 'productBarcode',
      width: 180,
      render: (_, record, index) => (
        <Input
          value={record.productBarcode}
          onChange={(e) => updateItem(index, 'productBarcode', e.target.value)}
          placeholder="產品條碼"
          prefix={<BarcodeOutlined />}
        />
      ),
    },
    {
      title: '包裝數量',
      key: 'quantityPackage',
      width: 100,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          value={record.quantityPackage}
          onChange={(value) => updateItem(index, 'quantityPackage', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '可下單數量',
      key: 'availableStock',
      width: 110,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={record.availableStock && record.availableStock > 0 ? 'green' : 'red'}>
            {record.availableStock || 0}
          </Tag>
        </div>
      ),
    },
    {
      title: '出貨數量',
      key: 'quantityBaseUnit',
      width: 120,
      render: (_, record, index) => (
        <div>
          <InputNumber
            min={0}
            value={record.quantityBaseUnit}
            onChange={(value) => updateItem(index, 'quantityBaseUnit', value || 0)}
            style={{ width: '100%' }}
            status={record.isStockInsufficient ? 'error' : undefined}
          />
          {record.isStockInsufficient && (
            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
              <WarningOutlined /> 庫存不足 (可用: {record.availableStock})
            </div>
          )}
        </div>
      ),
    },
    {
      title: '品牌',
      key: 'brand',
      width: 100,
      render: (_, record, index) => (
        <Input
          value={record.brand}
          onChange={(e) => updateItem(index, 'brand', e.target.value)}
          placeholder="品牌"
        />
      ),
    },
    {
      title: '長度',
      key: 'length',
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={0.1}
          value={record.length}
          onChange={(value) => updateItem(index, 'length', value || 0)}
          style={{ width: '100%' }}
          placeholder="cm"
        />
      ),
    },
    {
      title: '寬度',
      key: 'width',
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={0.1}
          value={record.width}
          onChange={(value) => updateItem(index, 'width', value || 0)}
          style={{ width: '100%' }}
          placeholder="cm"
        />
      ),
    },
    {
      title: '高度',
      key: 'height',
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={0.1}
          value={record.height}
          onChange={(value) => updateItem(index, 'height', value || 0)}
          style={{ width: '100%' }}
          placeholder="cm"
        />
      ),
    },
    {
      title: '重量',
      key: 'weight',
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={0.1}
          value={record.weight}
          onChange={(value) => updateItem(index, 'weight', value || 0)}
          style={{ width: '100%' }}
          placeholder="kg"
        />
      ),
    },
    {
      title: '單價',
      key: 'unitPrice',
      width: 100,
      render: (_, record, index) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.unitPrice}
          onChange={(value) => updateItem(index, 'unitPrice', value || 0)}
          style={{ width: '100%' }}
          placeholder="¥"
        />
      ),
    },
    {
      title: '分配規則',
      key: 'allocateRule',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.allocateRule}
          onChange={(value) => updateItem(index, 'allocateRule', value)}
          style={{ width: '100%' }}
        >
          {allocateRules.map(rule => (
            <Option key={rule.value} value={rule.value}>{rule.label}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: '備註',
      key: 'remark',
      width: 150,
      render: (_, record, index) => (
        <Input
          value={record.remark}
          onChange={(e) => updateItem(index, 'remark', e.target.value)}
          placeholder="備註"
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_, record, index) => (
        <Popconfirm
          title="確定要刪除此產品嗎？"
          onConfirm={() => removeItem(index)}
          okText="確定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={order ? '編輯出庫訂單' : '新增出庫訂單'}
      open={visible}
      onCancel={onCancel}
      width={1400}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          確定
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: '待發貨',
          countryCode: 'CN',
        }}
      >
        {hasStockIssues && (
          <Alert
            message="庫存不足警告"
            description={
              <div>
                <p>部分產品的出貨數量超過可下單數量。</p>
                <p>您仍然可以繼續創建此訂單，但訂單將被標記為庫存不足狀態。</p>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="倉庫"
                name="warehouseId"
                rules={[{ required: true, message: '請選擇倉庫' }]}
              >
                <Select
                  placeholder="選擇倉庫"
                  value={warehouseId}
                  onChange={(v) => setWarehouseId(String(v))}
                  allowClear
                  showSearch
                  filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
                >
                  {(warehouses || []).map(w => (
                    <Option key={w.id} value={w.id}>{w.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="客戶名稱"
                name="customerName"
                rules={[{ required: true, message: '請輸入客戶名稱' }]}
              >
                <AutoComplete
                  options={customerOptions}
                  onSelect={handleCustomerSelect}
                  placeholder="搜尋客戶名稱"
                  filterOption={(inputValue, option) =>
                    option!.label.toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="客戶代碼"
                name="customerCode"
                rules={[{ required: true, message: '請選擇客戶' }]}
              >
                <Input disabled placeholder="自動填充" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="快遞商"
                name="courier"
              >
                <Select placeholder="選擇快遞商">
                  {couriers.map(courier => (
                    <Option key={courier} value={courier}>{courier}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="銷售渠道"
                name="salesChannel"
              >
                <Select placeholder="選擇銷售渠道">
                  <Option value="HKTV Mall">HKTV Mall</Option>
                  <Option value="Shopify">Shopify</Option>
                  <Option value="Amazon">Amazon</Option>
                  <Option value="eBay">eBay</Option>
                  <Option value="Taobao">Taobao</Option>
                  <Option value="Tmall">Tmall</Option>
                  <Option value="JD.com">JD.com</Option>
                  <Option value="Pinduoduo">Pinduoduo</Option>
                  <Option value="Lazada">Lazada</Option>
                  <Option value="Shopee">Shopee</Option>
                  <Option value="Direct Sales">Direct Sales</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="收件人"
                name="receiver"
              >
                <Input placeholder="收件人姓名" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="國家或地區"
                name="countryCode"
              >
                <Select placeholder="選擇國家或地區">
                  {countryCodes.map(country => (
                    <Option key={country.value} value={country.value}>
                      {country.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="配送追蹤號"
                name="deliveryTrackingNumber"
              >
                <Input placeholder="追蹤號碼" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="配送地址"
                name="deliveryAddress"
              >
                <TextArea rows={3} placeholder="詳細配送地址" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="配送備註"
                name="deliveryRemark"
              >
                <TextArea rows={3} placeholder="配送相關備註" />
              </Form.Item>
            </Col>
          </Row>

          

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="訂單備註"
                name="remark"
              >
                <TextArea rows={3} placeholder="訂單備註" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 產品清單 */}
        <Card 
          title={
            <Space>
              <ProductOutlined />
              產品清單
              <Badge count={items.length} style={{ backgroundColor: '#52c41a' }} />
            </Space>
          }
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
              添加產品
            </Button>
          }
        >
          <Table
            dataSource={items}
            columns={itemColumns}
            rowKey="id"
            pagination={false}
            scroll={{ x: 2000 }}
            size="small"
            locale={{
              emptyText: '請添加產品'
            }}
          />
          
          {items.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Space size="large">
                <Text strong>
                  產品總數: <Tag color="blue">{items.length}</Tag>
                </Text>
                <Text strong>
                  總數量: <Tag color="green">{items.reduce((sum, item) => sum + item.quantityBaseUnit, 0)}</Tag>
                </Text>
                <Text strong>
                  總金額: <Tag color="orange">¥{items.reduce((sum, item) => sum + (item.unitPrice * item.quantityBaseUnit), 0).toFixed(2)}</Tag>
                </Text>
                {hasStockIssues && (
                  <Text strong>
                    <Tag color="red" icon={<WarningOutlined />}>庫存問題</Tag>
                  </Text>
                )}
              </Space>
            </div>
          )}
        </Card>
      </Form>
    </Modal>
  );
};

export default OutboundOrderForm;